import { useTranslation } from 'react-i18next';
import { Link, Navigate, NavLink, Outlet, useLocation } from 'react-router-dom';
import { UpdateBanner } from '@/features/app-update';
import { useMobileShell } from '@/hooks/useMobileShell';
import { readRememberedSite } from '@/hooks/useCurrentSite';
import { backTarget, pickMobileTabs, twinPath } from '@/lib/routes/logic';
import { ROUTE_LABEL_KEY } from '@/lib/routes/route-labels';
import { useAuthStore } from '@/store/auth';

interface Tab {
  to: string;
  labelKey: string;
  end: boolean;
  entry: string;
}

/**
 * 手机端外壳：底部 Tab + 返回键。
 *
 * **最多五个，不做横向滚动。** 三条理由，每条都踩过：
 * 1. 375px 屏幕上再多每个就不到 62px，中文标签挤不下、戴手套也点不准；
 * 2. 底部那条正是安卓手势导航的返回/主页区域，在那儿横滑经常被系统判成「返回」；
 * 3. 底部栏没有滚动提示，划出屏幕的 Tab 基本不会被发现，等于白做。
 *    （横向滚动是**顶部** Tab 的标准模式，底部没有这个模式。）
 *
 * **底部栏是 首页 / 日报 / 隐蔽工程 / 采购 / 我的**（CR-044，`lib/routes/logic.ts` 的 `pickMobileTabs`）：
 * 首页 + 我的固定，中间三格位置固定、有权限才出现。待办、打卡、工地概览、图纸、进度表、考勤都是首页卡片。
 * **第一格对谁都叫「首页」**：改之前按权限叫「工地概览」或「首页」，其实是同一页；
 * 网页端的工地概览是另一页，现在是首页上的一张卡。
 * 改之前中间按「待办 → 资料 → 日报 → 采购」抢三个名额，全勾的角色丢采购、超管底部没有采购；
 * 「资料」是 CR-029 为省名额把图纸和进度表合成的只读页，上传另开两页藏在首页「其他」里 ——
 * 客户用 admin 都没找到「管理图纸」。离开底部栏后合并没有理由，拆回一模块一页，和网页端同一页。
 *
 * **返回键由这里统一画**（CR-044，客户原话「没有在底部tab的页面，app端需要有个返回的按键」）：
 * 路由表里写了 `parent` 的页面顶上有一条顶栏，左边是返回，写的是上一级的名字（「首页」「系统设置」），
 * 主色字、不画箭头（CR-082 按示例）；底部栏里的页面没有这条。页面标题由页面自己的 `PageHeader` 画成大标题。
 * 页面里别再自己写返回链接 —— 改之前各写各的，不在底部栏的 11 页里只有两页有。
 *
 * **宽屏上不渲染这套外壳**（CR-040）：用哪套外壳由设备决定，电脑浏览器是网页样式。
 * 旧链接、从手机样式拖宽窗口，都送到网页端对应的那一页。
 * App 里永远是这套（`useMobileShell` 先看 isNative），安卓平板也一样。
 */
export function MobileLayout() {
  const { t } = useTranslation();
  const role = useAuthStore((s) => s.user?.role ?? null);
  const isMobileShell = useMobileShell();
  const location = useLocation();

  if (!isMobileShell) {
    return (
      <Navigate
        to={twinPath(role, location.pathname, location.search, 'desktop', readRememberedSite())}
        replace
      />
    );
  }

  const tabs: Tab[] = [
    { to: '/m', labelKey: 'nav.home', end: true, entry: 'm.home' },
    ...pickMobileTabs(role).middle.map(({ route }) => ({
      to: route.path,
      labelKey: ROUTE_LABEL_KEY[route.id],
      end: false,
      entry: route.id,
    })),
    { to: '/m/me', labelKey: 'nav.me', end: false, entry: 'm.me' },
  ];

  const back = backTarget(location.pathname);

  return (
    // --sticky-bottom：贴底的保存栏（.sticky-bar）要让开底部 Tab
    <div className="flex min-h-full flex-col bg-paper [--sticky-bottom:calc(var(--touch-row)+env(safe-area-inset-bottom))]">
      {/* 顶栏（CR-082）：只有不在底部栏的页面有，左边返回到固定的上一级 */}
      {back ? (
        <header className="safe-top sticky top-0 z-nav border-b border-rule bg-paper/90 backdrop-blur">
          <div className="flex h-11 items-center px-2">
            <Link
              to={back.path}
              data-action="back"
              className="inline-flex min-h-touch items-center px-2 text-title font-medium text-brand-600"
            >
              {t(ROUTE_LABEL_KEY[back.id])}
            </Link>
          </div>
        </header>
      ) : (
        <div className="safe-top" />
      )}
      <main className="flex-1 px-4 pb-24 pt-4">
        {/* 有新版本时顶上一条（CR-067）。每一页都看得到，但不挡打卡也不挡底部 Tab */}
        <UpdateBanner />
        <Outlet />
      </main>

      <nav className="safe-bottom fixed inset-x-0 bottom-0 z-nav flex border-t border-rule bg-surface/90 backdrop-blur-lg">
        {tabs.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            end={tab.end}
            data-entry={tab.entry}
            className={({ isActive }) =>
              [
                'relative flex flex-1 flex-col items-center justify-center text-body font-medium',
                // 52px 高（CR-082：按系统标准，不再为手套放大到 56）
                'min-h-row transition-colors',
                // 没有图标（客户不要，CR-081）：当前这格靠主色字 + 顶上一小段主色条认出来
                isActive
                  ? 'font-semibold text-brand-600 before:absolute before:inset-x-5 before:top-0 before:h-0.5 before:rounded-full before:bg-brand-600'
                  : 'text-ink-3',
              ].join(' ')
            }
          >
            {t(tab.labelKey)}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
